import { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMediaQuery } from "react-responsive";

import { QUERY_WIDTHS, registerSchema } from "@utils/index";
import { RegisterFormInterface } from "@interfaces/index";
import { useCommonStore } from "@stores/index";
import { useUserRegister } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  Form,
  FormRow,
  FormInput,
  Button,
  FormHeaderNotAuth,
  ArrowLeftSvg,
  AuthLinks,
} from "@components/index";

export const RegisterForm = () => {
  const isAuth = useCommonStore(({ isAuth }) => isAuth);
  const setState = useCommonStore(({ setState }) => setState);
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const notify = useNotify();

  const { mutate, isPending: isPendingUserRegister } = useUserRegister(
    () => {
      reset();
      notify("success", "Your registration request was sent successfully!");
    },
    ({ message, fields }) => {
      Object.keys(fields || {}).forEach((key) =>
        setError(
          key as "firstName" | "lastName" | "email" | "phone" | "businessName",
          { message: fields![key] },
        ),
      );
      notify("error", message);
    },
  );

  useEffect(() => {
    setState({ isLoading: isPendingUserRegister });
  }, [isPendingUserRegister, setState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    setError,
    setValue,
  } = useForm<RegisterFormInterface>({
    mode: "onBlur",
    disabled: isPendingUserRegister,
    resolver: yupResolver(registerSchema),
  });

  const onSubmit: SubmitHandler<RegisterFormInterface> = (data) =>
    mutate({ ...data });

  if (isAuth) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="md:mx-auto md:max-w-[440px] md:pt-[218px]"
      >
        <FormHeaderNotAuth
          title="Join FBTC"
          subTitle="Enter your details below"
        />
        <FormRow top="medium" isRow>
          <FormInput
            {...register<"firstName">("firstName")}
            name="firstName"
            label="First name"
            error={formState.errors.firstName?.message}
            onBlur={({ currentTarget }) => {
              setValue("firstName", currentTarget.value.trim());
            }}
            onChange={() => clearErrors("firstName")}
          />
          <FormInput
            {...register<"lastName">("lastName")}
            name="lastName"
            label="Last name"
            error={formState.errors.lastName?.message}
            onBlur={({ currentTarget }) => {
              setValue("lastName", currentTarget.value.trim());
            }}
            onChange={() => clearErrors("lastName")}
          />
        </FormRow>
        <FormRow>
          <FormInput
            {...register<"email">("email")}
            name="email"
            label="Email"
            error={formState.errors.email?.message}
            onChange={({ currentTarget }) => {
              setValue("email", currentTarget.value.trim());
              clearErrors("email");
            }}
          />
        </FormRow>
        <FormRow>
          <FormInput
            {...register<"phone">("phone")}
            name="phone"
            label="Phone number"
            type="phone"
            error={formState.errors.phone?.message}
            onChange={() => clearErrors("phone")}
          />
        </FormRow>
        <FormRow>
          <FormInput
            {...register<"businessName">("businessName")}
            name="businessName"
            label="Business description"
            error={formState.errors.businessName?.message}
            onBlur={({ currentTarget }) => {
              setValue("businessName", currentTarget.value.trim());
            }}
            onChange={() => clearErrors("businessName")}
          />
        </FormRow>
        <FormRow top="medium">
          <Button type="submit" title="Submit" />
        </FormRow>
        <FormRow top="small">
          <Link
            to="/login"
            className="mx-auto flex w-fit items-center gap-x-2 text-200 font-bold text-black-54 hover:underline"
          >
            <ArrowLeftSvg />
            Back to sign in
          </Link>
        </FormRow>
        {isMobile && (
          <FormRow top="large">
            <AuthLinks />
          </FormRow>
        )}
      </Form>
      {!isMobile && <AuthLinks />}
    </>
  );
};
